
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from "react";
import { translate } from "../services/AppService";
import * as echarts from "echarts";
import * as app from "../services/AppService";
import { Select, Switches,Button } from "../components";

// ── Types ────────────────────────────────────────────────────────────────────
export interface ChartData {
    name: string;
    value: number;
}

interface PieChartProps {
    title?: string;
    tooltipName: string;
    subTitle?: string;
    id?: string;
    height_px?: string;
    data: ChartData[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────
function getCssVar(v: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(v).trim();
}
function buildLegend(
    showLegend: boolean,
    textColor: string,
    tooltipBg: string,
    tooltipBorder: string,
    tooltipColor: string,
    data: ChartData[],
    withTooltip = false
): echarts.LegendComponentOption {
    return {
        show: showLegend,
        type: "scroll",
        orient: "horizontal",
        top: "4%",
        left: "center",
        width: "60%",
        pageTextStyle: { color: textColor },
        textStyle: { color: textColor },
        ...(withTooltip && {
            tooltip: {
                show: true,
                backgroundColor: tooltipBg,
                borderColor: tooltipBorder,
                textStyle: { color: tooltipColor },
                formatter: (params: any) => {
                    const item = data.find(d => d.name === params.name);
                    if (!item) return params.name;
                    const total = data.reduce((sum, d) => sum + d.value, 0);
                    const pct = ((item.value / total) * 100).toFixed(1);
                    return `
                        <div style="font-weight:600;margin-bottom:4px">${item.name}</div>
                        <div>Value: <strong>${item.value}</strong></div>
                        <div>Share: <strong>${pct}%</strong></div>
                    `;
                },
            },
        }),
    };
}

/** Shared slice emphasis (shadow on hover) */
const emphasisStyle: echarts.PieSeriesOption["emphasis"] = {
    itemStyle: {
        shadowBlur: 10,
        shadowOffsetX: 0,
        shadowColor: "rgba(0,0,0,0.5)",
    },
};

// ── Chart option factory ─────────────────────────────────────────────────────
function getChartOption(
    type: string,
    data: ChartData[],
    tooltipName: string,
    colors: string[],
    textColor: string,
    tooltipBg: string,
    tooltipBorder: string,
    tooltipColor: string,
    showLegend: boolean
): echarts.EChartsOption {

    const baseConfig: echarts.EChartsOption = {
        backgroundColor: "transparent",
        color: colors,
        textStyle: { color: textColor, fontSize: 12 },
        tooltip: {
            trigger: "item",
            confine: true,
            backgroundColor: tooltipBg,
            borderColor: tooltipBorder,
            textStyle: { color: tooltipColor },
            formatter: `{a} <br/>{b} : {c} ({d}%)`,
        },
    };

    // Shorthand tuple for buildLegend common args
    const legendArgs = [showLegend, textColor, tooltipBg, tooltipBorder, tooltipColor, data] as const;

    switch (type) {

        // ── Pie (standard) ───────────────────────────────────────────
        case "0":
        default:
            return {
                ...baseConfig,
                legend: buildLegend(...legendArgs),
                series: [{
                    name: translate(tooltipName),
                    type: "pie",
                    radius: "70%",
                    center: ["50%", showLegend ? "55%" : "50%"],
                    data,
                    label: { color: textColor },
                    emphasis: emphasisStyle,
                }],
            }
        // ── Doughnut ─────────────────────────────────────────────────
        case "1":
            return {
                ...baseConfig,
                legend: buildLegend(...legendArgs, true),
                series: [{
                    name: translate(tooltipName),
                    type: "pie",
                    radius: ["40%", "70%"],
                    avoidLabelOverlap: false,
                    label: { show: false, position: "center" },
                    emphasis: {
                        ...emphasisStyle,
                        label: { show: true, fontSize: 40, fontWeight: "bold" },
                    },
                    labelLine: { show: false },
                    data,
                }],
            };
        // ── Rose ─────────────────────────────────────────────────
        case "2":
            return {
                ...baseConfig,
                legend: buildLegend(...legendArgs, true),
                series: [{
                    name: translate(tooltipName),
                    type: "pie",
                    roseType: "area",        
                    radius: [20, "70%"],   
                    center: ["50%", showLegend ? "55%" : "50%"],
                    padAngle: 3,
                    itemStyle: {
                        borderRadius: 6,
                    },
                    label: { color: textColor },
                    emphasis: emphasisStyle,
                    data,
                }],
            };
    }
}

// ── Component ────────────────────────────────────────────────────────────────
function PieChart(props: PieChartProps) {
    const [chart, setChart] = useState<echarts.ECharts | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<HTMLDivElement>(null);

    const [model, setModel] = useState<string>("2");  // "0"=Pie  "1"=Doughnut  "2"=Rose
    const [control, setControl] = useState<boolean>(true);  // "0"=Display  "1"=Hide

    // ── 1. Init echarts instance once ───────────────────────────────
    useEffect(() => {
        if (!chartRef.current) return;
        echarts.getInstanceByDom(chartRef.current)?.dispose();

        const instance = echarts.init(chartRef.current, null, {
            renderer: "canvas",
            useDirtyRect: false,
        });
        setChart(instance);
        return () => {
            instance.dispose();
            setChart(null);
        };
    }, []);

    // ── 2. Build & apply ECharts options ────────────────────────────
    const applyOptions = useCallback(() => {
        if (!chart || props.data.length === 0) return;

        // Wait until the DOM has real dimensions
        if (chartRef.current) {
            const { width, height } = chartRef.current.getBoundingClientRect();
            if (width === 0 || height === 0) {
                setTimeout(applyOptions, 100);
                return;
            }
        }

        const dark = app.getCookie("colorMode", "dark") === "dark";
        const textColor = dark ? "rgba(220,220,220,0.9)" : "rgba(50,50,50,0.9)";
        const tooltipBg = dark ? "#1e2d3d" : "#ffffff";
        const tooltipBorder = dark ? "#2d4a5a" : "#dee2e6";

        const colors = [
            getCssVar("--bs-primary"),
            getCssVar("--bs-success"),
            getCssVar("--bs-info"),
            getCssVar("--bs-warning"),
            getCssVar("--bs-danger"),
            getCssVar("--bs-secondary"),
        ];

        chart.setOption(
            getChartOption(
                model,
                props.data,
                props.tooltipName,
                colors,
                textColor,
                tooltipBg,
                tooltipBorder,
                textColor,       
                control 
            ),
            true
        );

        setTimeout(() => chart.resize(), 50);
    }, [chart, props.data, props.tooltipName, model, control]);

    useEffect(() => { applyOptions(); }, [applyOptions]);

    // ── 3. React to Bootstrap light/dark theme toggles ──────────────
    useEffect(() => {
        const observer = new MutationObserver(applyOptions);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-bs-theme", "class"],
        });
        return () => observer.disconnect();
    }, [applyOptions]);

    // ── 4. Handle window & container resize ─────────────────────────
    const handleResize = useCallback(() => { chart?.resize(); }, [chart]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => {
            chart?.resize();
            applyOptions();
        });
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [chart, applyOptions]);


 // ── Toolbar actions ──────────────────────────────────────────────
    //! function to hande save as Image:
    const handleSaveImage = useCallback(() => {
        if (!chart) return;
        const url = chart.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "transparent" });
        const a = document.createElement("a");
        a.href = url;
        a.download = `${props.title ?? "chart"}.png`;
        a.click();
    }, [chart, props.title]);

   //! function to handle view the data:
    const handleDataView = useCallback(() => {
        if (!chart) return;
        const total = props.data.reduce((sum, d) => sum + d.value, 0);
        const rows = props.data
            .map(d => {
                const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0";
                return `<tr>
                <td class="px-3 py-1">${d.name}</td>
                <td class="px-3 py-1" style="text-align:right">${d.value}</td>
                <td class="px-3 py-1" style="text-align:right">${pct}%</td>
            </tr>`;
            })
            .join("");
        const html = `
        <html><head><title>Data View</title>
        <style>
            body { font-family: sans-serif; padding: 20px; }
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ccc; text-align: left; padding: 6px 12px; }
            th { background: #f0f0f0; }
            tr:nth-child(even) { background: #f9f9f9; }
        </style>
        </head><body>
        <h3>${translate(props.title)}</h3>
        <table>
            <thead><tr>
                <th>Name</th>
                <th style="text-align:right">Value</th>
                <th style="text-align:right">Share</th>
            </tr></thead>
            <tbody>${rows}</tbody>
            <tfoot><tr>
                <td><strong>Total</strong></td>
                <td style="text-align:right"><strong>${total}</strong></td>
                <td style="text-align:right"><strong>100%</strong></td>
            </tr></tfoot>
        </table>
        </body></html>`;
        const w = window.open("", "_blank", "width=500,height=400");
        w?.document.write(html);
        w?.document.close();
    }, [chart, props.data, props.title]);


    // ── Render ─────────────────────────────────
    const hasData = props.data.length > 0;

    return (
        <div
            ref={containerRef}
            className="overflow-hidden rounded"
            style={{
                border: hasData ? "1px solid var(--bs-primary-border-subtle)" : "none",
                background: "transparent",
            }}
        >
            {hasData && (
                <div
                    className="px-2 bg-primary-subtle py-2 d-flex flex-column gap-2"
                    style={{ borderBottom: "1px solid var(--bs-primary)" }}
                >
                    {/* ── Row 1: Controls (Select + Switch) + Action Buttons ── */}
                    <div className="d-flex align-items-center gap-2">

                        {/* Chart type */}
                        <div style={{ minWidth: 120, flex: "0 0 auto" }}>
                            <Select
                                class="w-100"
                                options={app.GetKeyValueArr(["Pie", "Doughnut", "Rose"])}
                                model={model}
                                onChange={setModel}
                            />
                        </div>
                        <div
                            style={{ flex: "0 0 auto" }}
                            className="bg-info-subtle border border-1 border-info rounded px-1 d-flex align-items-center gap-2">
                            <Switches
                                colsize="auto"
                                name="LastMatch"
                                checked={control}
                                onChange={setControl}
                            />
                        </div>

                        {/* Push buttons to the right */}
                        <div className="ms-auto d-flex align-items-center gap-2">
                            <Button
                                label="View"
                                labelClass="text-danger"
                                faIcon="fa-solid fa-table-list text-danger"
                                class="bg-danger-subtle border border-1 border-danger"
                                clicked={handleDataView}
                            />
                            <Button
                                label="Download"
                                labelClass="text-warning"
                                faIcon="fa-solid fa-file-image text-warning"
                                class="bg-warning-subtle border border-1 border-warning"
                                clicked={handleSaveImage}
                            />
                        </div>
                    </div>

                    {/* ── Row 2: Title + Subtitle centered ── */}
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <span className="fw-bold fs-5" style={{ color: "var(--bs-light)" }}>
                            {translate(props.title)}
                        </span>
                        {props.subTitle && (
                            <span className="opacity-75 small" style={{ color: "var(--bs-dark)" }}>
                                {translate(props.subTitle)}
                            </span>
                        )}
                    </div>
                </div>
            )}
            <div
                ref={chartRef}
                style={{
                    width: "100%",
                    height: props.height_px ? `${props.height_px}px` : "clamp(220px, 40vw, 500px)",
                    display: hasData ? "block" : "none",
                }}
            />
        </div>
    );
}

export default PieChart;